import CancelButton from "components/CancelButton";
import ConfirmBoxModal from "components/ConfirmBoxModal";
import SubmitHookFormTS from "components/SubmitHookFormTS";
import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useRecoilState } from "recoil";
import { PolicyTermType } from "../../types";
import {
  addStatePolicyTerm,
  editStatePolicyTerm,
  policyTermSaving,
  rowEditIdStatePolicyTerm,
  selectedRowsStatePolicyTerm,
} from "../atoms/atoms";
import useAddPolicyTerm from "../hooks/useAddPolicyTerm";
import useEditPolicyTerm from "../hooks/useUpdatePolicyTerm";
import PolicyTermInput from "./PolicyTermInput";

export const PolicyTermForm = () => {
  // atoms
  const [editData, setEditData] = useRecoilState(rowEditIdStatePolicyTerm);
  const [isSaving, setIsSaving] = useRecoilState(policyTermSaving);
  const [isAddState, setIsAddState] = useRecoilState(addStatePolicyTerm);
  const [isEditState, setIsEditState] = useRecoilState(editStatePolicyTerm);
  const [, setSelectedRows] = useRecoilState(selectedRowsStatePolicyTerm);

  // hooks
  const addPolicyTerm = useAddPolicyTerm();
  const updatePolicyTerm = useEditPolicyTerm();

  // local
  const [newData, setNewData] = useState<PolicyTermType>();
  const [saveModal, setSaveModal] = useState(false);
  const [changes, setChanges] = useState(false);

  // form methods
  const useFormMethods = useForm({
    defaultValues: { ...(editData as unknown as PolicyTermType) },
  });

  const {
    formState: { errors },
  } = useFormMethods;

  const onSubmit = (data: PolicyTermType) => {
    if (data.polTerm <= 0) {
      useFormMethods.setError("polTerm", {
        message: "Policy Term must be greater than 0",
      });
      return;
    }
    const value = {
      ...data,
      policyTermName: `${data.polTerm} ${
        data.polTerm <= 1 ? "month" : "months"
      }`,
      polTerm: +data.polTerm,
    };

    setNewData(value);
    setSaveModal(true);
    setIsSaving(true);
  };

  const handleCancel = () => {
    useFormMethods.reset();
    useFormMethods.clearErrors();
    setEditData(null);
    setIsAddState(false);
    setIsEditState(false);
    setSelectedRows([]);
  };

  const handleSave = async () => {
    if (newData) {
      if (isEditState) {
        await updatePolicyTerm(newData);
      } else {
        await addPolicyTerm(newData);
      }
    }
    useFormMethods.reset();
    useFormMethods.clearErrors();
    setEditData(null);
    setIsAddState(false);
    setIsEditState(false);
  };

  return (
    <>
      <div className="px-2 py-2 space-y-2 border-l-2 border-primary box-shadow">
        <FormProvider {...useFormMethods}>
          <form onSubmit={useFormMethods.handleSubmit(onSubmit)}>
            <div className="flex flex-row  justify-between items-center text-quinary font-bold font-sans text-lg pb-1">
              <div className="ml-2 font-bold text-primary text-base">
                {isAddState ? "Add New Policy Term" : "Edit Policy Term"}
              </div>
              <div className="flex justify-between gap-2 mr-2">
                <SubmitHookFormTS
                  type="submit"
                  disabled={!changes}
                  buttonText={isSaving ? "Saving.." : "Save"}
                />
                <CancelButton
                  onClick={(event) => {
                    event.preventDefault();
                    handleCancel();
                  }}
                />
              </div>
            </div>
            <div className="mx-2 border-b-2 border-secondary"></div>
            <div className="text-xs text-secondary italic font-normal mt-2 ml-2">
              <b> Note:</b> All fields with * are required to fill.
            </div>
          </form>
          <div>
            <PolicyTermInput
              data={editData!}
              setChanges={setChanges}
              errorMessage={errors.polTerm?.message}
            />
          </div>
        </FormProvider>
      </div>

      {saveModal && (
        <ConfirmBoxModal
          ModalTitle={isAddState ? "Add Policy Term" : "Update Policy Term"}
          qConfirmation={`Are you sure you want to ${
            isAddState ? "add " : "update "
          } this data?`}
          isOpen={saveModal}
          onClose={() => {
            setSaveModal(false);
            setIsSaving(false);
          }}
          onClickFunction={() => {
            setSaveModal(false);
            handleSave();
            setIsSaving(false);
          }}
          cancelButtonRef={undefined}
        />
      )}
    </>
  );
};
